import { Controller } from "@hotwired/stimulus"

// Operator sets per field type — mirrors ReportFilter::{STRING,NUMERIC}_OPERATORS + OPERATOR_LABELS.
// Pickable string fields also get ["any_of", "is any of"], appended in fieldChanged.
const OPERATORS = {
  string: [["contains", "contains"], ["equals", "equals"], ["starts_with", "starts with"]],
  numeric: [["gt", ">"], ["lt", "<"], ["eq", "="]],
}

// The `field operator value` filter builder (Google-Ads style). Filters are AND-combined; each is a
// card keeping a hidden filters[] token synced from its inputs (blank cards are disabled so they don't
// submit). Changing the field swaps the operator set + value control by the field's data-type/pickable;
// the "is any of" operator swaps the text input for a searchable checkbox list of specific entities.
export default class extends Controller {
  static targets = ["rows", "template"]
  static values = { optionsUrl: String }

  connect() {
    this.rowsTarget.querySelectorAll("[data-filter-row]").forEach((row) => this.applyOperatorMode(row))
  }

  add() {
    const row = this.templateTarget.content.firstElementChild.cloneNode(true)
    this.rowsTarget.appendChild(row)
    this.applyOperatorMode(row)
  }

  remove(event) {
    event.target.closest("[data-filter-row]")?.remove()
  }

  fieldChanged(event) {
    const row = event.target.closest("[data-filter-row]")
    if (!row) return

    const option = event.target.selectedOptions[0]
    const type = option?.dataset.type || "string"
    const pickable = option?.dataset.pickable === "true"
    const operators = (type === "string" && pickable) ? [...OPERATORS.string, ["any_of", "is any of"]] : OPERATORS[type]

    const operatorSelect = row.querySelector("[data-filter-operator]")
    operatorSelect.innerHTML = operators.map(([v, l]) => `<option value="${v}">${l}</option>`).join("")
    row.querySelector("[data-filter-value]").setAttribute("inputmode", type === "numeric" ? "decimal" : "text")

    this.applyOperatorMode(row)
    this.sync(event)
  }

  operatorChanged(event) {
    this.applyOperatorMode(event.target.closest("[data-filter-row]"))
    this.sync(event)
  }

  // Show the checkbox picker for "is any of", the text input otherwise.
  applyOperatorMode(row) {
    if (!row) return

    const anyOf = row.querySelector("[data-filter-operator]").value === "any_of"
    if (anyOf) this.ensurePicker(row)
    row.querySelector("[data-filter-picker]")?.classList.toggle("hidden", !anyOf)
    row.querySelector("[data-filter-value]").classList.toggle("hidden", anyOf)
  }

  // Fetch a row's picker options for its selected field's entity type, on demand (only when "is any
  // of" is chosen), rebuilding when the field switches to a different entity. A saved selection
  // (server-rendered any_of row) is restored once the list loads. Options come from
  // FilterOptionsController rather than a server-rendered template, so the report page never eager-
  // loads them.
  async ensurePicker(row) {
    const list = row.querySelector("[data-filter-picker-list]")
    if (!list) return

    const entityType = row.querySelector("[data-filter-field]").selectedOptions[0]?.dataset.entityType || ""
    if (list.dataset.entityType === entityType && list.dataset.loaded === "true") return

    list.dataset.entityType = entityType
    list.dataset.loaded = ""
    list.innerHTML = `<div class="v2-loader v2-loader-sm px-2 py-1.5">Loading…</div>`

    let data
    try {
      const url = `${this.optionsUrlValue}&entity_type=${encodeURIComponent(entityType)}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error(`filter options ${response.status}`)
      data = await response.json()
    } catch {
      list.innerHTML = `<p class="text-xs text-sg-error-dark px-2 py-1.5">Couldn't load options.</p>`
      return
    }

    // The field may have changed again while the request was in flight; only render if still current.
    if (list.dataset.entityType !== entityType) return

    const selected = new Set((row.dataset.selectedIds || "").split(",").filter(Boolean))
    list.innerHTML = ""
    data.options.forEach(([id, name]) => list.appendChild(this.buildPickerItem(id, name, selected.has(String(id)))))
    if (data.capped) {
      const note = document.createElement("p")
      note.className = "text-xs text-sg-gray-500 px-2 py-1.5"
      note.textContent = `Showing the first ${data.options.length.toLocaleString()} — use "contains" to match more.`
      list.appendChild(note)
    }
    list.dataset.loaded = "true"
    this.sync({ target: list }) // reflect any restored selection into the hidden token
  }

  // A single option row (checkbox + name). Built in JS so the entity name is set via textContent
  // (never interpolated as HTML).
  buildPickerItem(id, name, checked) {
    const label = document.createElement("label")
    label.dataset.pickerItem = ""
    label.className = "flex items-center gap-2 px-2 py-1.5 hover:bg-sg-gray-50 cursor-pointer"

    const input = document.createElement("input")
    input.type = "checkbox"
    input.value = String(id)
    input.className = "checkbox shrink-0"
    input.checked = checked
    input.dataset.action = "change->reporting--filter-builder#sync"

    const span = document.createElement("span")
    span.dataset.pickerName = ""
    span.className = "text-sm text-sg-gray-800 truncate"
    span.textContent = name

    label.append(input, span)
    return label
  }

  filterPicker(event) {
    const query = event.target.value.trim().toLowerCase()
    event.target.closest("[data-filter-picker]").querySelectorAll("[data-picker-item]").forEach((item) => {
      const name = item.querySelector("[data-picker-name]").textContent.toLowerCase()
      item.classList.toggle("hidden", query !== "" && !name.includes(query))
    })
  }

  // Check every currently-visible option (i.e. whatever the search has narrowed to), then sync.
  selectAllVisible(event) {
    event.target.closest("[data-filter-picker]")
      .querySelectorAll("[data-picker-item]:not(.hidden) input[type=checkbox]")
      .forEach((cb) => { cb.checked = true })
    this.sync(event)
  }

  // Uncheck the whole list (including options hidden by the current search).
  clearPicker(event) {
    event.target.closest("[data-filter-picker]")
      .querySelectorAll("input[type=checkbox]")
      .forEach((cb) => { cb.checked = false })
    this.sync(event)
  }

  sync(event) {
    const row = event.target.closest("[data-filter-row]")
    if (!row) return

    const fieldSelect = row.querySelector("[data-filter-field]")
    const operator = row.querySelector("[data-filter-operator]").value
    const token = row.querySelector("[data-filter-token]")

    let value
    if (operator === "any_of") {
      value = Array.from(row.querySelectorAll("[data-filter-picker-list] input:checked")).map((cb) => cb.value).join(",")
    } else {
      const numeric = fieldSelect.selectedOptions[0]?.dataset.type === "numeric"
      const input = row.querySelector("[data-filter-value]")
      value = input.value.trim()
      if (numeric) {
        value = value.replace(/[^\d.]/g, "")
        input.value = this.grouped(value)
      }
    }

    if (value === "") {
      token.value = ""
      token.disabled = true
    } else {
      token.value = `${fieldSelect.value}|${operator}|${encodeURIComponent(value)}`
      token.disabled = false
    }
  }

  // "5000000" -> "5,000,000" (preserves a trailing/decimal part as typed).
  grouped(number) {
    if (number === "") return ""
    const dot = number.indexOf(".")
    const whole = dot === -1 ? number : number.slice(0, dot)
    const fraction = dot === -1 ? "" : number.slice(dot)
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + fraction
  }
}
